import { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import {
  ArrowRight,
  Award,
  Building2,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";
import { CLINIC, IMAGES } from "../lib/content";

const fade = (delay = 0, y = 20) => ({
  initial: { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] },
});

const float = (delay = 0) => ({
  animate: { y: [0, -8, 0] },
  transition: { duration: 6, delay, repeat: Infinity, ease: "easeInOut" },
});

// Two path shapes the wavy pink shape morphs between, for slow organic motion.
const WAVE_A =
  "M140,0 C40,140 100,320 90,470 C80,620 200,720 240,900 L700,900 L700,0 Z";
const WAVE_B =
  "M170,0 C60,170 60,330 120,490 C170,640 210,720 220,900 L700,900 L700,0 Z";
const WAVE_C =
  "M120,0 C30,150 130,300 80,460 C40,610 220,730 260,900 L700,900 L700,0 Z";

export default function Hero() {
  const stageRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const slides = IMAGES.slideshow || [];

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setSlide((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length]);

  // Mouse tracked as -0.5 → 0.5 across the stage, spring-smoothed.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 70, damping: 18, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 70, damping: 18, mass: 0.6 });

  const onMouseMove = (e) => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  // Per-card parallax offsets — hooks must be top-level (no helper).
  const cardAffiliatedX = useTransform(sx, [-0.5, 0.5], [-36, 36]);
  const cardAffiliatedY = useTransform(sy, [-0.5, 0.5], [-22, 22]);
  const cardYearsX = useTransform(sx, [-0.5, 0.5], [28, -28]);
  const cardYearsY = useTransform(sy, [-0.5, 0.5], [-18, 18]);
  const cardSubSpecX = useTransform(sx, [-0.5, 0.5], [-42, 42]);
  const cardSubSpecY = useTransform(sy, [-0.5, 0.5], [18, -18]);
  const cardRoyalX = useTransform(sx, [-0.5, 0.5], [32, -32]);
  const cardRoyalY = useTransform(sy, [-0.5, 0.5], [22, -22]);
  const cardRatingX = useTransform(sx, [-0.5, 0.5], [-18, 18]);
  const cardRatingY = useTransform(sy, [-0.5, 0.5], [30, -30]);
  const cardBadgeX = useTransform(sx, [-0.5, 0.5], [40, -40]);
  const cardBadgeY = useTransform(sy, [-0.5, 0.5], [-26, 26]);
  const portraitX = useTransform(sx, [-0.5, 0.5], [14, -14]);
  const portraitOffsetY = useTransform(sy, [-0.5, 0.5], [10, -10]);

  // Portrait scroll parallax retained.
  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section
      id="top"
      className="relative pt-20 sm:pt-24 pb-12 sm:pb-20 bg-[#F1EBE4]"
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 lg:px-8">
        <motion.div
          {...fade(0, 24)}
          ref={stageRef}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          className="relative bg-white rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] overflow-hidden"
          style={{
            boxShadow:
              "0 60px 120px -40px rgba(228,71,122,0.20), 0 20px 40px -20px rgba(94,39,65,0.12)",
          }}
        >
          {/* Animated wavy pink shape */}
          <div className="absolute inset-y-0 right-0 w-[58%] pointer-events-none">
            <svg
              viewBox="0 0 700 900"
              preserveAspectRatio="none"
              className="w-full h-full"
            >
              <defs>
                <linearGradient id="pinkWave" x1="0" x2="1" y1="0" y2="1">
                  <stop offset="0" stopColor="#F9E1E8" />
                  <stop offset="0.55" stopColor="#EDB9C4" />
                  <stop offset="1" stopColor="#D68A9C" />
                </linearGradient>
              </defs>
              <motion.path
                fill="url(#pinkWave)"
                initial={{ d: WAVE_A }}
                animate={{ d: [WAVE_A, WAVE_B, WAVE_C, WAVE_A] }}
                transition={{
                  duration: 16,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </svg>
          </div>

          {/* Faint arc lines */}
          <svg
            className="absolute inset-0 pointer-events-none"
            viewBox="0 0 1400 900"
            preserveAspectRatio="none"
          >
            <path
              d="M-200,720 Q400,300 1600,760"
              stroke="#5E2741"
              strokeOpacity="0.05"
              strokeWidth="1"
              fill="none"
            />
            <path
              d="M-100,820 Q500,420 1700,860"
              stroke="#5E2741"
              strokeOpacity="0.04"
              strokeWidth="1"
              fill="none"
            />
          </svg>

          {/* Content grid */}
          <div className="relative z-10 grid lg:grid-cols-[1.05fr_1fr] gap-6 sm:gap-8 lg:gap-2 items-start px-5 sm:px-10 lg:px-14 pt-6 sm:pt-10 lg:pt-16 pb-10 sm:pb-12">
            {/* Left */}
            <div className="order-2 lg:order-1 pt-2 lg:pt-6">
              <motion.span
                {...fade(0.1)}
                className="overline text-accent block mb-4 sm:mb-6"
              >
                Obstetrics &amp; Gynaecology · 25 years
              </motion.span>

              <motion.h1
                {...fade(0.2)}
                className="font-serif tracking-tight leading-[0.98] sm:leading-[0.95] text-[1.95rem] xs:text-[2.25rem] sm:text-[3.4rem] lg:text-[5.4rem]"
              >
                <span className="block text-primary">Women&rsquo;s health</span>
                <span
                  className="block italic"
                  style={{
                    background:
                      "linear-gradient(90deg,#E4477A 0%,#C63367 45%,#8E2447 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  &amp; motherhood,
                </span>
                <span className="block text-foreground">in gentle hands.</span>
              </motion.h1>

              <motion.p
                {...fade(0.35)}
                className="mt-6 sm:mt-8 max-w-md text-muted-foreground text-sm sm:text-base lg:text-lg leading-relaxed"
              >
                Compassionate, evidence-led consultations with{" "}
                <span className="text-foreground font-semibold">
                  {CLINIC.doctor}
                </span>{" "}
                — {CLINIC.credentialsShort}. Based at {CLINIC.name},
                Mahadevapura.
              </motion.p>

              <motion.a
                {...fade(0.5)}
                href="/#booking"
                data-testid="hero-book-btn"
                className="group mt-8 sm:mt-10 inline-flex items-center gap-3 sm:gap-4 rounded-full bg-accent text-accent-foreground pl-1.5 sm:pl-2 pr-6 sm:pr-8 py-1.5 sm:py-2 hover:-translate-y-0.5 transition-transform"
                style={{
                  boxShadow: "0 18px 40px -12px rgba(228,71,122,0.55)",
                }}
              >
                <span className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-white text-accent grid place-items-center group-hover:rotate-45 transition-transform duration-500">
                  <ArrowRight size={18} />
                </span>
                <span className="font-sans font-extrabold uppercase tracking-tight text-[0.78rem] sm:text-sm">
                  Book an appointment
                </span>
              </motion.a>

              {/* Mobile/tablet stat strip */}
              <motion.div
                {...fade(0.65)}
                className="mt-10 grid grid-cols-2 gap-3 lg:hidden"
              >
                <div className="rounded-2xl border border-border bg-white p-4">
                  <p className="font-serif text-primary text-2xl leading-none">
                    25 yrs
                  </p>
                  <p className="text-[0.7rem] text-muted-foreground mt-2">
                    Clinical practice
                  </p>
                </div>
                <div className="rounded-2xl border border-border bg-white p-4">
                  <p className="font-serif text-primary text-lg leading-tight">
                    MRCOG · MRCPI
                  </p>
                  <p className="text-[0.7rem] text-muted-foreground mt-1">
                    London &amp; Ireland
                  </p>
                </div>
                <div className="rounded-2xl border border-border bg-white p-4">
                  <p className="text-xs font-semibold text-foreground leading-tight">
                    Fertility &amp; Aesthetic Gynaecology
                  </p>
                  <p className="text-[0.65rem] text-muted-foreground mt-1">
                    FRM · FMAS
                  </p>
                </div>
                <div className="rounded-2xl border border-border bg-white p-4">
                  <div className="flex items-center gap-1.5">
                    <span className="flex text-accent">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={11}
                          fill="currentColor"
                          strokeWidth={0}
                        />
                      ))}
                    </span>
                    <span className="text-sm font-semibold text-foreground">
                      5.0
                    </span>
                  </div>
                  <p className="text-[0.7rem] text-muted-foreground mt-1.5">
                    180 Google reviews
                  </p>
                </div>
              </motion.div>

              <motion.div
                {...fade(0.75)}
                className="hidden lg:flex mt-14 items-center gap-8"
              >
                <div className="flex items-center gap-2">
                  <span className="flex text-accent">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        fill="currentColor"
                        strokeWidth={0}
                      />
                    ))}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">5.0</span> ·
                    180 Google reviews
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck size={16} className="text-accent" />
                  MRCOG (London) &amp; MRCPI (Ireland)
                </div>
              </motion.div>
            </div>

            {/* Right — portrait + floating cards */}
            <div className="order-1 lg:order-2 relative min-h-[320px] sm:min-h-[500px] lg:min-h-[640px]">
              <motion.div
                {...fade(0.25, 40)}
                style={{ x: portraitX, y: portraitOffsetY }}
                className="relative h-full flex items-end justify-center"
              >
                <div className="relative h-[320px] sm:h-[500px] lg:h-[640px] aspect-[4/5] max-w-full">
                  <AnimatePresence mode="sync">
                    <motion.img
                      key={slides[slide] || IMAGES.portrait}
                      src={slides[slide] || IMAGES.portrait}
                      alt="Mom and More Clinic — Dr. Yasmin at work"
                      initial={{ opacity: 0, scale: 1.06 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 1.02 }}
                      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                      style={{ y: portraitY }}
                      className="absolute inset-0 w-full h-full object-cover rounded-2xl drop-shadow-[0_30px_40px_rgba(94,39,65,0.15)]"
                    />
                  </AnimatePresence>

                  {slides.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" data-testid="hero-slideshow-dots">
                      {slides.map((_, i) => (
                        <button
                          key={i}
                          aria-label={`Show image ${i + 1}`}
                          onClick={() => setSlide(i)}
                          className={`h-1.5 rounded-full transition-all duration-500 ${i === slide ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                          style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Affiliated */}
              <motion.div
                {...fade(0.9)}
                style={{ x: cardAffiliatedX, y: cardAffiliatedY }}
                className="hidden lg:block absolute top-8 left-2 sm:left-6 z-20"
              >
                <motion.div
                  {...float(0)}
                  className="bg-white rounded-2xl px-4 py-3 border border-border shadow-lg flex items-center gap-3 max-w-[15rem]"
                >
                  <span className="w-9 h-9 rounded-lg bg-secondary/70 text-accent grid place-items-center">
                    <Building2 size={17} />
                  </span>
                  <div className="leading-tight">
                    <p className="text-[0.65rem] font-extrabold text-muted-foreground uppercase tracking-wider">
                      Affiliated
                    </p>
                    <p className="text-xs font-semibold text-foreground mt-1">
                      Rainbow Hospitals · BirthRight
                    </p>
                  </div>
                </motion.div>
              </motion.div>

              {/* Royal College */}
              <motion.div
                {...fade(1.05)}
                style={{ x: cardRoyalX, y: cardRoyalY }}
                className="hidden lg:block absolute -bottom-4 left-0 sm:left-4 z-20"
              >
                <motion.div
                  {...float(1.5)}
                  className="bg-white rounded-2xl px-4 py-3.5 border border-border shadow-lg max-w-[18rem]"
                >
                  <div className="flex items-center gap-2">
                    <Award size={15} className="text-accent" />
                    <p className="text-[0.65rem] font-extrabold text-muted-foreground uppercase tracking-wider">
                      Royal College accreditation
                    </p>
                  </div>
                  <p className="font-serif text-primary text-lg mt-1.5 leading-tight">
                    MRCOG <span className="text-accent">·</span> MRCPI
                  </p>
                  <p className="text-[0.7rem] text-muted-foreground mt-0.5">
                    London (UK) &amp; Ireland
                  </p>
                </motion.div>
              </motion.div>

              {/* 5.0 pill */}
              <motion.div
                {...fade(1.2)}
                style={{ x: cardRatingX, y: cardRatingY }}
                className="hidden lg:block absolute -bottom-6 left-1/2 -translate-x-1/2 z-20"
              >
                <motion.div
                  {...float(2.5)}
                  className="flex items-center gap-3 bg-white rounded-full pl-3 pr-5 py-2.5 border border-border shadow-lg"
                >
                  <span className="flex text-accent">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        fill="currentColor"
                        strokeWidth={0}
                      />
                    ))}
                  </span>
                  <div className="leading-none">
                    <p className="text-xs font-semibold text-foreground">
                      <span className="text-accent">5.0</span> · 180 reviews
                    </p>
                    <p className="text-[0.6rem] text-muted-foreground mt-0.5">
                      on Google
                    </p>
                  </div>
                </motion.div>
              </motion.div>

              {/* Dr. Yasmin badge — always visible */}
              <motion.div
                {...fade(0.8)}
                style={{ x: cardBadgeX, y: cardBadgeY }}
                className="absolute bottom-6 sm:bottom-10 lg:bottom-14 right-1 sm:right-4 z-20"
              >
                <motion.div
                  {...float(1)}
                  className="bg-white rounded-2xl p-1.5 sm:p-2 border border-border shadow-xl"
                >
                  <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-lg sm:rounded-xl overflow-hidden bg-primary">
                    <img
                      src={IMAGES.portrait}
                      alt="Dr. Yasmin"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="mt-1.5 sm:mt-2 rounded-md sm:rounded-lg bg-primary text-primary-foreground text-center px-2 sm:px-3 py-1 sm:py-1.5">
                    <p className="text-[0.6rem] sm:text-[0.7rem] font-bold leading-none">
                      Dr. Yasmin
                    </p>
                    <p className="text-[0.5rem] sm:text-[0.55rem] opacity-80 mt-0.5 sm:mt-1 leading-none">
                      Senior Consultant · OBG
                    </p>
                  </div>
                </motion.div>
              </motion.div>

              {/* 25 years — always visible */}
              <motion.div
                {...fade(1.0)}
                style={{ x: cardYearsX, y: cardYearsY }}
                className="absolute top-2 sm:top-6 right-1 sm:right-4 z-20"
              >
                <motion.div
                  {...float(0.5)}
                  className="bg-white rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2 sm:py-3 border border-border shadow-lg flex items-center gap-2 sm:gap-3"
                >
                  <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-accent text-accent-foreground grid place-items-center">
                    <span className="font-serif text-base sm:text-xl leading-none">
                      25
                    </span>
                  </span>
                  <div className="leading-tight">
                    <p className="text-[0.7rem] sm:text-xs font-semibold text-foreground">
                      Years in practice
                    </p>
                    <p className="text-[0.6rem] sm:text-[0.65rem] text-muted-foreground mt-0.5">
                      Obstetrics &amp; Gynaecology
                    </p>
                  </div>
                </motion.div>
              </motion.div>

              {/* Sub-specialty */}
              <motion.div
                {...fade(1.35)}
                style={{ x: cardSubSpecX, y: cardSubSpecY }}
                className="hidden lg:block absolute top-1/2 -translate-y-8 left-0 sm:-left-2 z-20"
              >
                <motion.div
                  {...float(3.2)}
                  className="bg-white rounded-2xl px-4 py-3 border border-border shadow-lg max-w-[15rem]"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={13} className="text-accent" />
                    <p className="text-[0.65rem] font-extrabold text-muted-foreground uppercase tracking-wider">
                      Sub-specialty
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-foreground mt-1.5 leading-tight">
                    Fertility &amp; Aesthetic Gynaecology
                  </p>
                  <p className="text-[0.65rem] text-muted-foreground mt-0.5">
                    FRM · FMAS Fellowships
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
